import axios from "axios";
import { ArrowLeft, BarChart3, CreditCard, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { CarregandoBloco, CarregandoFormulario } from "@/components/carregando";
import { TIPOS_CLIENTE, tipoDaUrl } from "@/features/tipos-cliente";

import Premios from "./cartao/Premios";
import Previa from "./cartao/Previa";
import { Avisos, QuemGanha, ValidadeECombinacoes } from "./cartao/Regras";
import { Secao } from "./cartao/comum";
import { ROTA_FIDELIZE } from "./rotas";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
export const ROTA_RELATORIO_CARTAO = "/admin/impulsione/relatorio-ofertas/cartao-fidelidade";

/**
 * Fidelize seu cliente → Cartão Fidelidade (23/10/2026). Configura tudo de uma
 * vez e salva no botão; a prévia ao vivo usa o nome, o logo e o tema da loja.
 * Repaginada em 24/10/2026: etapas numeradas (1 prêmio, 2 quem ganha, 3
 * validade, 4 avisos), prêmios com foto, valores em R$ de caixa registradora.
 * Regras e motivos: docs/features/cartao-fidelidade e backend/api/v1/fidelidade/API.md.
 */
export default function CartaoFidelidadePage() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [salvando, setSalvando] = useState(false);
  // Erro ao abrir: aviso com "Tentar de novo" no lugar do carregamento sem fim.
  const [erro, setErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  const [params, setParams] = useSearchParams();
  // Vindo do "Criar oferta" da tela de Clientes (?para=recorrente): marca o tipo em
  // "Quem ganha carimbo" na 1ª carga; a barra "Alterações não salvas" pede para salvar
  const paraInicial = useRef(tipoDaUrl(params));
  const aplicar = (d) => {
    setDados(d);
    const para = paraInicial.current;
    paraInicial.current = null;
    if (para) {
      setCfg({ ...d.config, segmentos: [para] });
      toast.info(`Marcamos ${TIPOS_CLIENTE[para].nome} em "Quem ganha carimbo". Salve para valer.`);
      setParams((p) => { const n = new URLSearchParams(p); n.delete("para"); return n; }, { replace: true });
    } else {
      setCfg(d.config);
    }
  };
  useEffect(() => {
    setErro(false);
    axios.get("/api/fidelidade/config", auth()).then((r) => aplicar(r.data))
      .catch(() => { setErro(true); toast.error("Não foi possível carregar o cartão fidelidade"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `aplicar` só usa setters e a ref do ?para
  }, [tentativa]);

  const mudar = (campos) => setCfg((c) => ({ ...c, ...campos }));
  const alterado = useMemo(() => dados && cfg && JSON.stringify(cfg) !== JSON.stringify(dados.config), [dados, cfg]);
  const tema = useMemo(() => {
    if (!dados || !cfg) return null;
    const id = cfg.tema || dados.tema_do_nicho;
    return dados.temas.find((t) => t.id === id) || dados.temas[0];
  }, [dados, cfg]);

  const salvar = async (extra = {}) => {
    setSalvando(true);
    try {
      const r = await axios.put("/api/fidelidade/config", { ...cfg, ...extra }, auth());
      aplicar(r.data);
      toast.success(r.data.config.ativo ? "Cartão fidelidade salvo e ligado" : "Cartão fidelidade salvo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar");
    } finally {
      setSalvando(false);
    }
  };

  const pronto = !!(cfg && tema);
  const s = dados?.situacao || {};
  const ligado = !!dados?.config?.ativo;

  return (
    <div className="p-4 sm:p-6" data-e2e="fidelize-cartao">
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <button type="button" onClick={() => navigate(ROTA_FIDELIZE)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" /> Fidelize seu cliente
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-md">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold leading-tight">Cartão Fidelidade</h1>
              <p className="text-sm text-muted-foreground">10 carimbos, os 2 primeiros de presente. Na 8ª compra o sistema entrega o prêmio sozinho.</p>
            </div>
          </div>
        </div>

        {!pronto && (erro ? (
          <div className="rounded-2xl border bg-card p-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">Não foi possível carregar o cartão fidelidade.</p>
            <button type="button" onClick={() => setTentativa((t) => t + 1)}
              className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold hover:bg-muted">
              <RefreshCw className="w-4 h-4" /> Tentar de novo
            </button>
          </div>
        ) : (
          // Carregamento com cards (25/10/2026): situação, etapas e a prévia do cartão piscando.
          <>
            <CarregandoBloco altura="h-[116px]" />
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px] items-start">
              <div className="space-y-5 min-w-0">
                <CarregandoBloco altura="h-[430px]" />
                <CarregandoFormulario campos={3} />
              </div>
              <CarregandoBloco altura="h-[360px]" className="order-first lg:order-none" />
            </div>
          </>
        ))}

        {pronto && (<>

        <section className={`rounded-2xl border-2 p-4 sm:p-5 ${ligado ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card"}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Situação</p>
              <p className="text-2xl font-bold mt-0.5">{ligado ? "Ligado" : "Desligado"}</p>
              {(s.cartoes_abertos > 0 || s.premios_a_usar > 0) && (
                <p className="text-sm text-muted-foreground mt-0.5">
                  {s.cartoes_abertos} {s.cartoes_abertos === 1 ? "cartão aberto" : "cartões abertos"} · {s.cartoes_completos} {s.cartoes_completos === 1 ? "completo" : "completos"} · {s.premios_a_usar} {s.premios_a_usar === 1 ? "prêmio a usar" : "prêmios a usar"}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => navigate(ROTA_RELATORIO_CARTAO)}
                className="h-11 px-4 rounded-lg border font-semibold text-sm inline-flex items-center gap-2 hover:bg-muted">
                <BarChart3 className="w-4 h-4" /> Relatório
              </button>
              <button type="button" disabled={salvando} onClick={() => salvar({ ativo: !ligado })} data-e2e="ligar-cartao"
                className={`h-11 px-5 rounded-lg font-semibold disabled:opacity-60 ${ligado ? "border hover:bg-muted" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                {ligado ? "Desligar" : "Ligar o cartão"}
              </button>
            </div>
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px] items-start">
          <div className="space-y-5 min-w-0">
            <Premios premios={cfg.premios || []} produtos={dados.produtos || []} onChange={(premios) => mudar({ premios })} />
            <QuemGanha cfg={cfg} mudar={mudar} ticketMedio={dados.ticket_medio} porSegmento={dados.clientes_por_segmento || {}} />
            <ValidadeECombinacoes cfg={cfg} mudar={mudar} />
            <Avisos cfg={cfg} mudar={mudar} />
          </div>
          <div className="space-y-5 lg:sticky lg:top-4 order-first lg:order-none">
            <Secao titulo="Como o cliente vê">
              <Previa loja={dados.loja} tema={tema} premios={cfg.premios || []} temas={dados.temas}
                doNicho={dados.tema_do_nicho} onTema={(t) => mudar({ tema: t })} />
            </Secao>
          </div>
        </div>
        </>)}
      </div>

      {alterado && (
        // Barra presa no pé da área de conteúdo (sticky), não da janela: com "fixed" o
        // menu lateral cobria "Alterações não salvas".
        <div className="sticky bottom-0 z-30 mt-5 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 border-t bg-background/95 backdrop-blur p-3">
          <div className="max-w-6xl mx-auto flex items-center justify-end gap-3">
            <span className="text-sm text-muted-foreground mr-auto">Alterações não salvas</span>
            <button type="button" onClick={() => setCfg(dados.config)} className="h-10 px-4 rounded-lg border text-sm font-semibold hover:bg-muted">Desfazer</button>
            <button type="button" onClick={() => salvar()} disabled={salvando} data-e2e="salvar-cartao"
              className="h-10 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
              {salvando ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
